/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { random, randomItem } from "@indietabletop/appkit";
import type { NameGeneratorData } from "./Ruleset.ts";

export type NameTriple = [string, string, string];

type JoinFn = (
  firstName: string,
  lastNameStart: string,
  lastNameEnd: string
) => string;

export class NameGenerator {
  id: string;
  name: string;
  pattern: string;
  join: JoinFn;

  private firstNames: string[];
  private lastNameStart: string[];
  private lastNameEnd: string[];

  constructor(data: NameGeneratorData) {
    this.id = data.id;
    this.name = data.name;
    this.pattern = data.joinPattern;
    this.firstNames = data.names.map((n) => n[0]);
    this.lastNameStart = data.names.map((n) => n[1]);
    this.lastNameEnd = data.names.map((n) => n[2]);

    this.join = (start, middle, end) => {
      return data.joinPattern
        .replace(/{ *start *}/gi, start)
        .replace(/{ *middle *}/gi, middle)
        .replace(/{ *end *}/gi, end);
    };
  }

  generate() {
    const firstName = randomItem(this.firstNames)!;
    const lastNameStart = randomItem(this.lastNameStart)!;
    const lastNameEnd = randomItem(this.lastNameEnd)!;

    return this.join(firstName, lastNameStart, lastNameEnd);
  }

  fromTuple(f: number, l1: number, l2: number) {
    const start = this.firstNames[f] ?? this.firstNames[0]!;
    const middle = this.lastNameStart[l1] ?? this.lastNameStart[0]!;
    const end = this.lastNameEnd[l2] ?? this.lastNameEnd[0]!;

    return {
      start,
      middle,
      end,
    };
  }

  fromSearchParams(params: URLSearchParams) {
    const startIndex = params.get("start");
    const middleIndex = params.get("middle");
    const endIndex = params.get("end");

    if (!startIndex || !middleIndex || !endIndex) {
      return null;
    }

    return this.fromTuple(
      parseInt(startIndex, 10),
      parseInt(middleIndex, 10),
      parseInt(endIndex, 10)
    );
  }

  generateSearchParams(params?: URLSearchParams) {
    const start =
      params?.get("start") ?? random(this.firstNames.length).toString();

    const middle =
      params?.get("middle") ?? random(this.lastNameStart.length).toString();

    const end =
      params?.get("end") ?? random(this.lastNameEnd.length).toString();

    return new URLSearchParams({
      generator: this.id,
      start,
      middle,
      end,
    });
  }
}
